import { useCallback, useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import {
  SCIMConfigurationRead,
  SCIMConfigurationUpdate,
  ScimService,
  SCIMTokenCreateResponse,
  SCIMTokenRead,
} from 'api';
import { Logger } from 'utilities';
import { GroupMapping } from './useSCIMConfiguration.types';

const useSCIMConfiguration = ({ firmId }: { firmId: number | null }) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [scimConfiguration, setScimConfiguration] = useState<SCIMConfigurationRead | null>(null);
  const [isEnabled, setIsEnabled] = useState<boolean>(false);
  const [groupMappings, setGroupMappings] = useState<GroupMapping[]>([]);
  const [tokens, setTokens] = useState<SCIMTokenRead[] | null>(null);
  const [newlyCreatedToken, setNewlyCreatedToken] = useState<SCIMTokenCreateResponse | null>(null);

  const { enqueueSnackbar } = useSnackbar();

  const fetchSCIMConfiguration = useCallback(
    async (firmId: number) => {
      try {
        const response = await ScimService.apiScimConfigurationFirmRead(firmId.toString());
        setScimConfiguration(response);
      } catch (error) {
        Logger.error('Error fetching SCIM configuration:', error);
        enqueueSnackbar('Failed to fetch SCIM configuration.', { variant: 'error' });
        setScimConfiguration(null);
      }
    },
    [enqueueSnackbar]
  );

  const fetchTokens = useCallback(
    async (configurationId: number) => {
      try {
        setIsLoading(true);
        const response = await ScimService.apiScimConfigurationTokensList(configurationId.toString());
        setTokens(response.results);
      } catch (error) {
        Logger.error('Error fetching SCIM tokens:', error);
        enqueueSnackbar('Failed to fetch SCIM tokens.', { variant: 'error' });
        setTokens([]);
      } finally {
        setIsLoading(false);
      }
    },
    [enqueueSnackbar]
  );

  const updateSCIMConfiguration = useCallback(
    async (firmId: number, data: SCIMConfigurationUpdate) => {
      try {
        const response = await ScimService.apiScimConfigurationUpdate(firmId.toString(), data);
        setScimConfiguration(response);
        enqueueSnackbar('SCIM configuration updated successfully.', { variant: 'success' });
      } catch (error) {
        Logger.error('Error updating SCIM configuration:', error);
        enqueueSnackbar('Failed to update SCIM configuration.', { variant: 'error' });
      }
    },
    [enqueueSnackbar]
  );

  useEffect(() => {
    if (firmId !== null) {
      fetchSCIMConfiguration(firmId);
    }
  }, [firmId, fetchSCIMConfiguration]);

  useEffect(() => {
    if (scimConfiguration) {
      setIsEnabled(scimConfiguration.enabled!);
      setGroupMappings(
        scimConfiguration.groups.map(group => ({
          display_name: group.display_name,
          role_mapped: group.role_mapped,
          key: `${group.display_name}-${group.role_mapped}`,
        }))
      );
    }
  }, [scimConfiguration]);

  useEffect(() => {
    if (scimConfiguration?.id) {
      fetchTokens(scimConfiguration.id);
    }
  }, [scimConfiguration?.id, fetchTokens]);

  const generateToken = useCallback(async () => {
    if (!scimConfiguration) {
      return;
    }
    try {
      const response = await ScimService.apiScimConfigurationTokensCreate({
        scim_configuration: scimConfiguration.id!,
      });
      setNewlyCreatedToken(response);
      enqueueSnackbar('SCIM token generated successfully.', { variant: 'success' });
      await fetchTokens(scimConfiguration.id!);
    } catch (error) {
      Logger.error('Error generating SCIM token:', error);
      enqueueSnackbar('Failed to generate SCIM token.', { variant: 'error' });
    }
  }, [scimConfiguration, enqueueSnackbar, fetchTokens]);

  const rotateToken = useCallback(
    async (tokenId: number) => {
      if (!scimConfiguration) {
        return;
      }
      try {
        setIsLoading(true);
        await ScimService.apiScimConfigurationTokensRotationCreate({
          token: tokenId,
          scim_configuration: scimConfiguration.id as number,
        });
        enqueueSnackbar('SCIM token rotated successfully.', { variant: 'success' });
        await fetchTokens(scimConfiguration.id!);
      } catch (error) {
        Logger.error('Error rotating SCIM token:', error);
        enqueueSnackbar('Failed to rotate SCIM token.', { variant: 'error' });
      } finally {
        setIsLoading(false);
      }
    },
    [enqueueSnackbar, scimConfiguration, fetchTokens]
  );

  const revokeToken = useCallback(
    async (tokenId: number) => {
      if (!scimConfiguration) {
        return;
      }
      try {
        setIsLoading(true);
        await ScimService.apiScimConfigurationTokensRevokationCreate({
          token: tokenId,
          scim_configuration: scimConfiguration.id as number,
        });
        enqueueSnackbar('SCIM token revoked successfully.', { variant: 'success' });
        await fetchTokens(scimConfiguration.id!);
      } catch (error) {
        Logger.error('Error revoking SCIM token:', error);
        enqueueSnackbar('Failed to revoke SCIM token.', { variant: 'error' });
      } finally {
        setIsLoading(false);
      }
    },
    [enqueueSnackbar, scimConfiguration, fetchTokens]
  );

  return {
    scimConfiguration,
    isEnabled,
    setIsEnabled,
    groupMappings,
    setGroupMappings,
    tokens,
    setTokens,
    fetchSCIMConfiguration,
    updateSCIMConfiguration,
    fetchTokens,
    newlyCreatedToken,
    setNewlyCreatedToken,
    generateToken,
    isLoading,
    rotateToken,
    revokeToken,
  };
};

export default useSCIMConfiguration;
